import type { HttpClient } from '../client/http';
import { SdkError } from '../client/errors';
import { urlBase64ToUint8Array } from './urlBase64';
import { createReminders, type RemindersMethods } from './reminders';

/**
 * P14 SDK — Web Push API.
 *
 * Usage no app:
 *
 *   const { push } = useHook();
 *   if (!push.isAvailable()) return showInstallPrompt();
 *   await push.subscribe();   // dispara permission prompt do browser + salva no backend
 *   push.status();            // 'granted' | 'denied' | 'prompt' | 'unsupported'
 *   await push.unsubscribe(); // remove subscription do backend + browser
 *
 * Dependências externas:
 *   - navigator.serviceWorker.getRegistration() deve retornar um SW já
 *     registrado (P15 cuida do registro; P14 assume existência).
 *   - PushManager e Notification APIs nativas do browser.
 *   - GET /api/push/vapid-public-key (público).
 *   - POST /api/app/:appId/push/subscribe (auth cookie via credentials:include).
 *   - DELETE /api/app/:appId/push/unsubscribe (idem).
 *
 * iOS: Safari só suporta PushManager se app foi instalado como PWA
 * (Add to Home Screen). isAvailable() retorna false se requisitos faltam;
 * caller mostra prompt de install antes de tentar subscribe.
 */

export type PushStatus = 'granted' | 'denied' | 'prompt' | 'unsupported';

export interface PushMethods {
  /** Sync — verifica se APIs necessárias estão disponíveis no ambiente. */
  isAvailable(): boolean;
  /** Estado atual da permission. 'unsupported' se APIs faltam. */
  status(): PushStatus;
  /**
   * Dispara permission prompt (se necessário) + subscribe no push service +
   * POST pro backend. Retorna `{ subscribed: true }` em sucesso.
   *
   * @throws SdkError('push.unsupported')        — APIs faltam (iOS sem PWA)
   * @throws SdkError('push.permission_denied')  — user bloqueou
   * @throws SdkError('push.sw_not_registered')  — SW ainda não registrou (P15 faz isso)
   * @throws SdkError('push.backend_rejected')   — backend retornou erro
   */
  subscribe(): Promise<{ subscribed: true }>;
  /**
   * Remove subscription do browser + DELETE no backend. Idempotente —
   * sem subscription ativa retorna `{ subscribed: false }` sem erro.
   */
  unsubscribe(): Promise<{ subscribed: false }>;
  /**
   * CRUD sobre reminders + agendamento ad-hoc + fallbacks.
   * Ver `./reminders.ts` pros detalhes de cada método.
   */
  reminders: RemindersMethods;
}

function env() {
  // jsdom/SSR: globals podem faltar. Proteções:
  const nav = typeof navigator !== 'undefined' ? navigator : undefined;
  const win = typeof window !== 'undefined' ? window : undefined;
  return { nav, win };
}

function hasApis(): boolean {
  const { nav, win } = env();
  return !!(nav && 'serviceWorker' in nav && win && 'PushManager' in win && 'Notification' in win);
}

async function fetchVapidPublicKey(client: HttpClient): Promise<string> {
  const res = await client.request<{ publicKey: string }>('GET', '/api/push/vapid-public-key');
  if (!res.publicKey) {
    throw new SdkError('VAPID public key missing in response', 'push.vapid_unavailable', 0);
  }
  return res.publicKey;
}

export function createPush(client: HttpClient, appId: string): PushMethods {
  const subscribePath = `/api/app/${appId}/push/subscribe`;
  const unsubscribePath = `/api/app/${appId}/push/unsubscribe`;
  const reminders = createReminders(client, appId);

  return {
    reminders,

    isAvailable(): boolean {
      return hasApis();
    },

    status(): PushStatus {
      if (!hasApis()) return 'unsupported';
      const perm = (Notification as unknown as { permission: NotificationPermission }).permission;
      if (perm === 'granted') return 'granted';
      if (perm === 'denied') return 'denied';
      return 'prompt';
    },

    async subscribe(): Promise<{ subscribed: true }> {
      if (!hasApis()) {
        throw new SdkError('Web Push not supported in this browser', 'push.unsupported', 0);
      }

      const registration = await navigator.serviceWorker.getRegistration();
      if (!registration) {
        // Audit Wave 3 — Fix #34. SW only registers in PROD (frontend/shell
        // main.tsx gates on import.meta.env.PROD to avoid HMR conflicts).
        // Loud warn beats silent fail when devs hit subscribe() in dev mode.
        console.warn(
          '[hook-sdk] Push subscribe blocked: navigator.serviceWorker.getRegistration() returned null. ' +
            'Push notifications require a registered Service Worker. ' +
            'In Vite dev mode this is expected — frontend/shell registers the SW only when import.meta.env.PROD is true (HMR conflict). ' +
            'To test push locally: `cd frontend/shell && bun run build && bun run preview`.',
        );
        throw new SdkError(
          'Service Worker not registered — call register() before push.subscribe()',
          'push.sw_not_registered',
          0,
        );
      }

      // Permission — browser só aceita prompt dentro de user gesture.
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') {
        throw new SdkError(`Notification permission: ${perm}`, 'push.permission_denied', 0);
      }

      const vapidPublicKey = await fetchVapidPublicKey(client);
      const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);

      // Se já houver subscription, reutiliza em vez de criar nova (browser
      // retorna mesmo endpoint pra mesmo VAPID key + device).
      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          // Uint8Array<ArrayBuffer> satisfaz BufferSource em runtime. TS 5.x
          // ficou rigoroso com ArrayBuffer vs SharedArrayBuffer (TS 5.6+).
          // Cast é seguro aqui — urlBase64ToUint8Array retorna ArrayBuffer-backed.
          applicationServerKey: applicationServerKey as BufferSource,
        });
      }

      const json = subscription.toJSON() as {
        endpoint: string;
        keys: { p256dh: string; auth: string };
      };
      if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
        throw new SdkError('Invalid subscription payload from browser', 'push.invalid_subscription', 0);
      }

      const userAgent =
        typeof navigator !== 'undefined' && typeof navigator.userAgent === 'string'
          ? navigator.userAgent.slice(0, 500)
          : undefined;

      try {
        await client.request('POST', subscribePath, {
          body: {
            endpoint: json.endpoint,
            keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
            ...(userAgent ? { userAgent } : {}),
          },
        });
      } catch (err) {
        // Audit Wave 3 — Fix #11. Rollback the browser-side subscription so
        // we don't leave it orphaned (browser subscribed, backend doesn't
        // know → push silently fails forever). Also covers reused-sub case:
        // failed POST = source-of-truth disagreement, so resync by clearing
        // both sides and letting the next subscribe() round re-acquire.
        try {
          await subscription.unsubscribe();
        } catch {
          // Best-effort. If unsubscribe fails, propagate the original POST
          // error — caller's UX is the same either way.
        }
        throw err instanceof SdkError
          ? err
          : new SdkError(String(err), 'push.backend_rejected', 0);
      }

      return { subscribed: true };
    },

    async unsubscribe(): Promise<{ subscribed: false }> {
      if (!hasApis()) return { subscribed: false };

      const registration = await navigator.serviceWorker.getRegistration();
      const subscription = registration
        ? await registration.pushManager.getSubscription()
        : null;

      if (subscription) {
        const endpoint = subscription.endpoint;
        try {
          await client.request('DELETE', unsubscribePath, { body: { endpoint } });
        } catch {
          // Backend DELETE é idempotente (204 mesmo se endpoint não existe).
          // Erros de rede não devem bloquear unsubscribe local.
        }
        try {
          await subscription.unsubscribe();
        } catch {
          /* ignore */
        }
      }

      return { subscribed: false };
    },
  };
}
